import React, { ReactNode, useState } from 'react';

interface TabProps {
  title: ReactNode;
  value: number;
  selected?: number;
  onTabClick?: (index: number) => void;
  className?: string;
  isFocused?: boolean;
}

const Tab: React.FC<TabProps> = ({
  title,
  value,
  selected,
  onTabClick,
  className = '',
  isFocused,
}) => (
  <div
    role="button"
    onClick={() => onTabClick?.(value)}
    className={`cursor-pointer px-4 py-2 text-center ${
      selected === value ? 'bg-[#0e80b4] text-white' : 'bg-white text-black'
    } border border-[#0e80b4] ${
      isFocused && selected === value ? 'outline outline-black outline-2 z-10' : ''
    } ${className}`}
  >
    {title}
  </div>
);

interface TabStripProps {
  selected: number;
  onSelect: (e: { selected: number }) => void;
  className?: string;
  children: ReactNode;
}

const TabStrip: React.FC<TabStripProps> = ({ children, onSelect, selected, className }) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleTabClick = (index: number) => {
    onSelect({ selected: index });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    let newSelected = selected;

    if (e.key === 'ArrowRight') {
      newSelected = (selected + 1) % React.Children.count(children);
      onSelect({ selected: newSelected });
      e.preventDefault();
    }

    if (e.key === 'ArrowLeft') {
      newSelected =
        (selected - 1 + React.Children.count(children)) % React.Children.count(children);
      onSelect({ selected: newSelected });
      e.preventDefault();
    }
  };

  const updatedChildren = React.Children.map(children, (child, index) => {
    if (React.isValidElement<TabProps>(child)) {
      return React.cloneElement<TabProps>(child, {
        onTabClick: handleTabClick,
        selected,
        isFocused,
      });
    }
    return child;
  });

  return (
    <div
      className={`flex ${className} outline-none`}
      onKeyDown={handleKeyDown}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      tabIndex={0}
    >
      {updatedChildren}
    </div>
  );
};

export { Tab, TabStrip };
